import PropTypes from "prop-types";
import { useState, useEffect, useContext } from "react";
import { gql, useMutation } from "@apollo/client";
import { Modal, Card, Icon, Checkbox, Chip, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import { MessageManager } from "context";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "92%", sm: "70%", md: "55%", xxl: "45%" },
    overflowY: "auto",
    p: 3,
};

const UPDATE_TAREA_ITEM = gql`
    mutation updateTareaItem($idItem: ID!, $checked: Boolean, $observacion: String) {
        updateTareaItem(idItem: $idItem, checked: $checked, observacion: $observacion) {
            id
            estado
            items {
                id
                texto
                checked
                observacion
            }
        }
    }
`;

export default function ModalDetalleTarea({ open, onClose, task, onUpdated }) {
    const { handleSnackbar } = useContext(MessageManager);
    const [localItems, setLocalItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [updateTareaItem] = useMutation(UPDATE_TAREA_ITEM);

    useEffect(() => {
        if (task && task.items) {
            setLocalItems(task.items.map(it => ({
                id: it.id,
                texto: it.texto,
                checked: !!it.checked,
                observacion: it.observacion || ""
            })));
        } else {
            setLocalItems([]);
        }
    }, [task]);

    const handleCheck = (idx, checked) => {
        setLocalItems(prevItems => prevItems.map((item, i) =>
            i === idx ? { ...item, checked } : item
        ));
    };

    const handleLocalObsChange = (idx, val) => {
        setLocalItems(prevItems => prevItems.map((item, i) =>
            i === idx ? { ...item, observacion: val } : item
        ));
    }

    const handleSave = async () => {
        setSaving(true);
        try {
            const promises = [];
            // Compare localItems with task.items (original)
            localItems.forEach((localItem) => {
                const original = task.items.find(t => t.id === localItem.id);
                if (!original) return;

                const checkedChanged = localItem.checked !== !!original.checked;
                const obsChanged = (localItem.observacion || "") !== (original.observacion || "");

                // Always send update if checked changed or observation changed.
                // Note: The prompt says "todas aquellas tareas marcadas como chequeadas y las no chequeadas, deben ser persistidas"
                // This implies persisting the current state. Since we only start with the initial state, we just need to save what's different or save all if needed?
                // Saving only differences is more efficient and safer.

                if (checkedChanged || obsChanged) {
                    promises.push(updateTareaItem({
                        variables: {
                            idItem: localItem.id,
                            checked: localItem.checked,
                            observacion: localItem.observacion
                        }
                    }));
                }
            });

            if (promises.length > 0) {
                await Promise.all(promises);
                handleSnackbar("Cambios guardados correctamente", "success");
                onUpdated?.();
            } else {
                // No changes, just close
                handleSnackbar("No hubo cambios", "info");
            }

            onClose();

        } catch (e) {
            handleSnackbar(e.message || "Error al guardar cambios", "error");
        } finally {
            setSaving(false);
        }
    };

    if (!task) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <Card sx={style}>
                <SoftBox display="flex" justifyContent="center" alignItems="center" mb={2} position="relative">
                    <SoftTypography variant="h5" fontWeight="medium">
                        Detalles de la tarea
                    </SoftTypography>
                    {/* Currently title centered per sketch? Sketch has it centered. */}
                </SoftBox>

                <SoftBox mb={3} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                    <SoftTypography variant="h5" fontWeight="bold">
                        {task.titulo}
                    </SoftTypography>
                    {task.tag?.nombre && (
                        <Chip
                            label={task.tag.nombre}
                            variant="contained"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 'bold', borderColor: '#000103ff' }}
                        />
                    )}
                </SoftBox>

                <SoftBox mb={3}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                        Observación:
                    </SoftTypography>
                    {task.observacion ? (
                        <SoftBox mt={1} sx={{ border: '1px solid #4e4e4e4b', backgroundColor: '#f5f5f5', borderRadius: 2, p: 2 }}>
                            <SoftTypography variant="body2" color="text">
                                {task.observacion}
                            </SoftTypography>
                        </SoftBox>
                    ) : (
                        <SoftBox mt={1} sx={{ border: '1px solid #4e4e4e4b', backgroundColor: '#f5f5f5', borderRadius: 2, p: 2 }}>
                            <SoftTypography variant="caption" color="text" fontStyle="italic">
                                Sin observación
                            </SoftTypography>
                        </SoftBox>
                    )}
                </SoftBox>

                <SoftBox component="div">
                    <SoftTypography variant="h6" fontWeight="bold" color="text" mb={2} textTransform="uppercase">
                        Items
                    </SoftTypography>
                    <SoftBox display="flex" flexDirection="column" gap={2}>
                        {localItems.map((item, idx) => (
                            <Card key={item.id} sx={{ p: 2, border: "1px solid #dee2e6", boxShadow: "none !important" }}>
                                <SoftBox display="flex" alignItems="center" mb={1}>
                                    <Checkbox
                                        checked={!!item.checked}
                                        onChange={(e) => handleCheck(idx, e.target.checked)}
                                    />
                                    <SoftTypography
                                        variant="button"
                                        fontWeight="medium"
                                        color="text"
                                        sx={{
                                            flex: 1,
                                            ml: 1
                                        }}
                                    >
                                        {item.texto}
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    placeholder="Observación (opcional)"
                                    value={item.observacion}
                                    onChange={(e) => handleLocalObsChange(idx, e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                            </Card>
                        ))}
                    </SoftBox>
                    {localItems.length === 0 && (
                        <SoftTypography variant="caption" color="text">No hay items</SoftTypography>
                    )}
                </SoftBox>

                <SoftBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <SoftButton variant="outlined" color="dark" onClick={onClose} disabled={saving}>
                        Cancelar
                    </SoftButton>
                    <SoftButton variant="gradient" color="dark" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : "Guardar"}
                    </SoftButton>
                </SoftBox>
            </Card>
        </Modal>
    );
}

ModalDetalleTarea.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    task: PropTypes.object,
    onUpdated: PropTypes.func,
};
